/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 30-06-2026
 *
 * Orchestrator voor node-contextmenu's. Bepaalt op basis van het node-type
 * welk sub-menu getoond wordt, via dezelfde type-hiërarchie-walk als de
 * ng1-`createNodeTypemenu` in `cytocanvas.modelview.controller.js`.
 *
 * Vervangt de qtip-gebaseerde node-menu's uit de ng1-controller.
 */
import {ChangeDetectionStrategy, Component} from '@angular/core';
import {elements} from 'dynalearn-common/lib/elements';
import {BaseNodeMenuComponent} from '../base-node-menu.component';
import {NodemenuCausalComponent} from './nodemenus/nodemenu-causal/nodemenu-causal.component';
import {NodemenuConfigurationComponent} from './nodemenus/nodemenu-configuration/nodemenu-configuration.component';
import {NodemenuCorrespondenceComponent} from './nodemenus/nodemenu-correspondence/nodemenu-correspondence.component';
import {NodemenuDCalcComponent} from './nodemenus/nodemenu-d-calc/nodemenu-d-calc.component';
import {NodemenuDIneqComponent} from './nodemenus/nodemenu-d-ineq/nodemenu-d-ineq.component';
import {NodemenuDerivativeComponent} from './nodemenus/nodemenu-derivative/nodemenu-derivative.component';
import {NodemenuDerivativePlusMinComponent} from './nodemenus/nodemenu-derivative-plus-min/nodemenu-derivative-plus-min.component';
import {NodemenuDerivativeZeroComponent} from './nodemenus/nodemenu-derivative-zero/nodemenu-derivative-zero.component';
import {NodemenuEntityAgentComponent} from './nodemenus/nodemenu-entity-agent/nodemenu-entity-agent.component';
import {NodemenuQCalcComponent} from './nodemenus/nodemenu-q-calc/nodemenu-q-calc.component';
import {NodemenuQExoComponent} from './nodemenus/nodemenu-q-exo/nodemenu-q-exo.component';
import {NodemenuQIneqComponent} from './nodemenus/nodemenu-q-ineq/nodemenu-q-ineq.component';
import {NodemenuQuantityComponent} from './nodemenus/nodemenu-quantity/nodemenu-quantity.component';
import {NodemenuQuantitySpaceComponent} from './nodemenus/nodemenu-quantity-space/nodemenu-quantity-space.component';
import {NodemenuQuantitySpaceIntervalComponent} from './nodemenus/nodemenu-quantity-space-interval/nodemenu-quantity-space-interval.component';
import {NodemenuQuantitySpacePointComponent} from './nodemenus/nodemenu-quantity-space-point/nodemenu-quantity-space-point.component';

/**
 * Mapping van node-types op alternatieve menu-namen (overrides).
 * Equivalent van de `nodemenus`-hash in `createNodeTypemenu`.
 */
const MENU_OVERRIDES: Record<string, string> = {
    entity: 'entity_agent',
    agent: 'entity_agent',
    derivative_plus: 'derivative_plus_min',
    derivative_min: 'derivative_plus_min'
};

/**
 * Set van beschikbare menu-namen. Equivalent van de AVAILABLE_MENUS-lijst
 * in `menu-template-loader.service.js`.
 */
const BESCHIKBARE_MENUS = new Set([
    'causal',
    'configuration',
    'correspondence_dqs',
    'correspondence_dv',
    'correspondence_qs',
    'correspondence_qv',
    'd_calc',
    'd_ineq',
    'derivative',
    'derivative_plus_min',
    'derivative_zero',
    'entity_agent',
    'q_calc',
    'q_exo',
    'q_ineq',
    'quantity',
    'quantity_space',
    'quantity_space_interval',
    'quantity_space_point'
]);

@Component({
    selector: 'dl-node-menu',
    standalone: true,
    imports: [
        NodemenuCausalComponent,
        NodemenuConfigurationComponent,
        NodemenuCorrespondenceComponent,
        NodemenuDCalcComponent,
        NodemenuDIneqComponent,
        NodemenuDerivativeComponent,
        NodemenuDerivativePlusMinComponent,
        NodemenuDerivativeZeroComponent,
        NodemenuEntityAgentComponent,
        NodemenuQCalcComponent,
        NodemenuQExoComponent,
        NodemenuQIneqComponent,
        NodemenuQuantityComponent,
        NodemenuQuantitySpaceComponent,
        NodemenuQuantitySpaceIntervalComponent,
        NodemenuQuantitySpacePointComponent
    ],
    templateUrl: './dl-node-menu.component.html',
    styleUrls: ['./dl-node-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DlNodeMenuComponent extends BaseNodeMenuComponent {

    constructor() {
        super();
    }

    /**
     * Bepaalt het te tonen menu-type op basis van het node-type, via
     * dezelfde hiërarchie-walk als ng1-`createNodeTypemenu`:
     * loopt opwaarts door de type-boom en geeft het eerste type terug
     * waarvoor een menu beschikbaar is (na toepassing van MENU_OVERRIDES).
     * Geeft null terug als geen menu gevonden is, en ook voor door de simulatie
     * getekende nodes: die dragen wel een echt elementtype (een ineq-waarde is
     * bijvoorbeeld `q_ineq_lt`), maar zijn geen modelelement om van type te wisselen.
     */
    protected get menuType(): string | null {
        const type = this.node?.data('type') as string | undefined;
        if (!type) return null;
        if (this.modelcanvas.isSimulatienode(this.node)) return null;
        for (const t of [type, ...elements.alleUps(type)]) {
            const mapped = MENU_OVERRIDES[t] ?? t;
            if (BESCHIKBARE_MENUS.has(mapped)) return mapped;
        }
        return null;
    }
}
